import React from "react";
import { AgGridReact } from "ag-grid-react";
import { formatExpiry } from "../../Helpers/SuppressionDuration";

/**
 * Task 9, suppress flow step 1 — choose which jobs the suppression applies to.
 *
 * Suppression is scoped to (organization, job, known error), so a job selection is mandatory:
 * "I must select at least one job before continuing". The guard follows the same shape as
 * components/JobControl/Step2.js — set an inline error and do NOT call props.nextStep().
 *
 * C4 — jobs that already have this error suppressed are marked here with the expiry that would be
 * replaced, so an overwrite is visible while choosing rather than only in the confirmation at the
 * end. The confirmation still runs; this just means it is never a surprise.
 */
export default function Step1SelectJobs({
    jobs,
    selectedJobIds,
    onSelectionChanged,
    /** [{ jobId, suppressedUntil }] for the known error being suppressed. */
    alreadySuppressed,
}) {

    const existingByJobId = new Map(
        (alreadySuppressed || []).map((s) => [s.jobId, s.suppressedUntil])
    );

    const columnDefs = [
        { field: "jobName", headerName: "Job", flex: 2, minWidth: 240 },
        {
            headerName: "Currently suppressed",
            field: "jobId",
            width: 200,
            sortable: false,
            cellRenderer: (params) => {
                if (!existingByJobId.has(params.value)) {
                    return <span className="text-muted">No</span>;
                }
                return (
                    <span style={{ color: "#8a6100" }}>
                        Yes — until {formatExpiry(existingByJobId.get(params.value))}
                    </span>
                );
            },
        },
        {
            field: "isEnabled",
            headerName: "Enabled",
            width: 110,
            valueFormatter: (params) => (params.value ? "Yes" : "No"),
        },
        { field: "lastStatus", headerName: "Last status", width: 140 },
    ];

    return (
        <div>
            <p className="text-muted">
                Choose the jobs this suppression should apply to. A known error can be suppressed on
                some jobs and left active on others.
            </p>

            <div className="ag-theme-alpine" style={{ marginBottom: 12 }}>
                <AgGridReact
                    rowData={jobs}
                    columnDefs={columnDefs}
                    defaultColDef={{ sortable: true, resizable: true }}
                    domLayout="autoHeight"
                    rowSelection={{ mode: "multiRow", checkboxes: true, headerCheckbox: true }}
                    onSelectionChanged={(e) =>
                        onSelectionChanged(e.api.getSelectedRows().map((r) => r.jobId))
                    }
                    onGridReady={(e) => {
                        // Re-apply the selection when stepping back from step 2, so the customer's
                        // choice is not silently lost by react-step-wizard remounting the step.
                        e.api.forEachNode((node) => {
                            if (selectedJobIds.includes(node.data.jobId)) node.setSelected(true);
                        });
                    }}
                    overlayNoRowsTemplate={
                        '<span class="ag-overlay-no-rows-center">You have no backup jobs</span>'
                    }
                />
            </div>

            {/*
              The "select at least one job" error this used to raise on click is gone: the
              dialog's footer disables Next until something is selected, so the invalid state
              is unreachable rather than reported after the fact.
            */}
            <div style={{ minHeight: 24 }}>
                {selectedJobIds.length > 0 && (
                    <span className="text-muted">
                        {selectedJobIds.length} job{selectedJobIds.length === 1 ? "" : "s"} selected
                    </span>
                )}
            </div>
        </div>
    );
}
